import photoService from "../../services/photoService";
import logger from "../../utils/logger";
import { getPendingPhotos, clearPendingPhotos } from "./photoStore";

/**
 * Background photo upload manager.
 *
 * Runs at the module level so uploads survive component unmounts
 * and SPA navigation. Only a full page close interrupts them.
 *
 * Usage:
 *   1. Call startUpload(bookingId) after booking creation
 *   2. Call subscribe(listener) from components to get progress updates
 *   3. Call getSnapshot() for the current state
 */

// ──────────────── State ────────────────

const IDLE = "idle";
const UPLOADING = "uploading";
const DONE = "done";
const FAILED = "failed";

let state = {
  status: IDLE, // 'idle' | 'uploading' | 'done' | 'failed'
  total: 0,
  completed: 0,
  failed: 0,
  warning: null,
};

let listeners = new Set();

function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((fn) => {
    try {
      fn(state);
    } catch (e) {
      /* swallow listener errors */
    }
  });
}

// ──────────────── Public API ────────────────

/**
 * Subscribe to upload state changes.
 * Returns an unsubscribe function.
 */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/**
 * Get current upload state (non-reactive snapshot).
 */
export function getSnapshot() {
  return { ...state };
}

/**
 * Begin uploading pending photos for a booking.
 * Safe to call even if there are no pending photos (no-op).
 */
export function startUpload(bookingId) {
  const photos = getPendingPhotos();
  if (photos.length === 0) {
    setState({
      status: DONE,
      total: 0,
      completed: 0,
      failed: 0,
      warning: null,
    });
    return;
  }

  setState({
    status: UPLOADING,
    total: photos.length,
    completed: 0,
    failed: 0,
    warning: null,
  });

  // Register beforeunload guard
  window.addEventListener("beforeunload", beforeUnloadGuard);

  // Upload each photo individually so we can track progress
  let completedCount = 0;
  let failedCount = 0;

  const uploads = photos.map((file) =>
    photoService
      .uploadPhoto(bookingId, file, "booking")
      .then(() => {
        completedCount++;
        setState({ completed: completedCount });
      })
      .catch((err) => {
        failedCount++;
        logger.error("Photo upload failed", { file: file.name, error: err });
        setState({ failed: failedCount });
      }),
  );

  Promise.all(uploads).then(() => {
    clearPendingPhotos();
    window.removeEventListener("beforeunload", beforeUnloadGuard);

    const warning =
      failedCount > 0
        ? `${failedCount} of ${photos.length} photo(s) could not be uploaded.`
        : null;

    setState({ status: failedCount > 0 ? FAILED : DONE, warning });

    logger.debug("Background photo upload finished", {
      total: photos.length,
      completed: completedCount,
      failed: failedCount,
    });
  });
}

/**
 * Reset the upload manager back to idle.
 */
export function resetUploadState() {
  window.removeEventListener("beforeunload", beforeUnloadGuard);
  state = { status: IDLE, total: 0, completed: 0, failed: 0, warning: null };
  listeners.clear();
}

// ──────────────── Internal ────────────────

function beforeUnloadGuard(e) {
  if (state.status === UPLOADING) {
    e.preventDefault();
    // Modern browsers ignore custom messages, but setting returnValue is required
    e.returnValue = "";
  }
}
